(ns manage.ad-variant.queries
  (:require [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.dispatcher :as dispatcher]
            [common.queries :as queries :refer [invalidate-queries]]
            [common.queries.http :as qhttp]
            [common.queries.muix-filter-util :as filter-util]
            [manage.ad-variant.mock :as mock]
            [re-frame.core :as rf]))

;; It is important to include parts of url-context that affect the resource URI
;; into the key. In this case it is there two IDs in addition to the api-url.
;; The api-url is static (mostly) so we don't need to include that.

(def active-fetch-interval 1000)

(def adset-content-build-refetch
  (queries/make-refetch-interval-fn
   {:interval-fn (fn [^js query fetch-count]
                   (let [ad-variant-creation-status (some-> (.-data (.-state query)) .-data ->clj :ad-variant-creation-status)
                         ;; If ad-variant-creation-status is not "completed", fetch every 1000ms.
                         ;; Otherwise, try 6 retries with increasing intervals from page load
                         ;; or invalidation to check if the status changes to "in progress" (after triggering renders).
                         ;;
                         ;; NOTE: If the ad-variant-creation is fast, we might see "completed" -> "completed",
                         ;; but with updated-at changing. We could stop when updated-at changes.
                         is-ad-variant-creation-active (not= "completed" ad-variant-creation-status)]
                     (if is-ad-variant-creation-active
                       active-fetch-interval
                       (if (< fetch-count queries/max-fetches)
                         (* (queries/sigmoid fetch-count) queries/max-fetch-interval)
                         0))))}))

(defn adset-content-build-query [{:keys [advertiser-id adset-id] :as context}]
  #js {:queryKey #js ["adset-content-build" advertiser-id adset-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset
                                           context
                                           {:get-latest-ad-variants-updated-at true})))
       :refetchInterval adset-content-build-refetch
       :enabled (and (some? advertiser-id) (some? adset-id))})

;; Run manage.ad_variant.queries.ENABLE_MOCK = true; in JS console and
;; refetch the query from TSQ devtools.
(defonce ENABLE_MOCK false)

(defn ad-variants-next-page-param [lastPage _pages]
  (let [next-cursor (:next-cursor (->clj lastPage))]
    ;; NOTE: Now this is making 1 extra request which returns empty data
    ;; TODO: Change BE to return the cursor only if there are more pages?
    ;; (js/console.log "next-cursor" (when next-cursor (js/atob next-cursor)))
    next-cursor))

(defn invalidate-ad-variant-queries! [advertiser-id adset-id]
  (invalidate-queries {:queryKey ["ad-variants" advertiser-id adset-id]})
  (invalidate-queries {:queryKey ["ad-variants-by-content-id" advertiser-id adset-id]})
  (invalidate-queries {:queryKey ["search-ad-variants" advertiser-id adset-id]})
  (invalidate-queries {:queryKey ["ad-variant-revisions" advertiser-id adset-id]})
  (invalidate-queries {:queryKey ["shared-ad-variants"]}))

(defn ad-variants-query [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["ad-variants" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [^js ctx]
                  (let [cursor (.-pageParam ctx)
                        cursor (when (not (zero? cursor))
                                 cursor)]
                    (-> (qhttp/get (qhttp/render :api/content-build-ad-variants
                                                 {:advertiser-id advertiser-id
                                                  :adset-id adset-id}
                                                 (if cursor
                                                   {:starting-after cursor}
                                                   {:sort "+created-at"})))

                        (cond-> ENABLE_MOCK (.then (fn [resp-data]
                                                     (mock/mock-ad-variants resp-data)))))))
       :initialPageParam nil
       :getNextPageParam ad-variants-next-page-param})

(def ad-variants-refetch
  (queries/make-refetch-interval-fn
   {:interval-fn (fn [query fetch-count]
                   (let [pages (some-> (.-data (.-state ^js query)) .-pages ->clj)
                         ad-variants (mapcat :data pages)
                         ad-variant-statuses (set (map #(get-in % [:data :ad-variant-status]) ad-variants))
                         ;; If some ad-variant is being rendered, keep fetching every 1000ms,
                         ;; else try 6 retries with increasing interval starting
                         ;; from page load or invalidation to see if any ad-variants' status
                         ;; goes to queued/rendering (which it goes after re-triggering renders).
                         interval (if (some ad-variant-statuses #{"queued" "rendering"})
                                    active-fetch-interval
                                    (if (< fetch-count queries/max-fetches)
                                      (* (queries/sigmoid fetch-count) queries/max-fetch-interval)
                                      0))]
                     interval))}))

(defn search-ad-variants-query
  [{:keys [advertiser-id adset-id] :as context}
   {:keys [default-sorting] :as filter-params
    :or {default-sorting {:field "content-id" :sort "desc"}}}
   {:keys [^js sort-model ^js filter-model] :as table-models}]
  #js {:enabled (some? adset-id)
       :queryKey #js ["search-ad-variants" advertiser-id adset-id sort-model filter-model]
       :queryFn (fn [^js ctx]
                  (qhttp/post (qhttp/render :api/content-build-search-ad-variants
                                            context)
                              {:body (filter-util/get-query-params
                                      (assoc filter-params :default-sorting default-sorting)
                                      table-models
                                      ctx)}))
       :getNextPageParam filter-util/default-get-next-page-param
       :staleTime (* 60 1000)
       :refetchInterval ad-variants-refetch})

(defn ad-variant-by-uuid-query
  [{:keys [advertiser-id adset-id ad-uuid]}]
  #js {:enabled (and (some? adset-id) (some? ad-uuid))
       :queryKey #js ["search-ad-variants" advertiser-id adset-id ad-uuid]
       :queryFn (fn []
                  (qhttp/post (qhttp/render :api/content-build-search-ad-variants
                                            {:advertiser-id advertiser-id
                                             :adset-id adset-id})
                              {:body {:limit 1
                                      :sort "+content-id"
                                      :ad-uuids [(str ad-uuid)]}}))
       :select (fn [^js response]
                 (get-in (->clj response) [:data 0 :data]))
       :staleTime (* 60 1000)})

(defn ad-variant-revisions-query
  [{:keys [advertiser-id adset-id ad-variant-uuid enabled]
    :or {enabled true}}]
  #js {:enabled (and (boolean enabled) (some? adset-id) (some? ad-variant-uuid))
       :queryKey #js ["ad-variant-revisions" advertiser-id adset-id ad-variant-uuid]
       :queryFn (fn [^js ctx]
                  (let [cursor (.-pageParam ctx)
                        cursor (when (not (zero? cursor)) cursor)]
                    (qhttp/get (qhttp/render :api/content-build-ad-variant-revisions
                                             {:advertiser-id advertiser-id
                                              :adset-id adset-id
                                              :ad-variant-uuid ad-variant-uuid}
                                             (if cursor
                                               {:starting-after cursor}
                                               {:sort "-created-at"
                                                :limit 100})))))
       :initialPageParam nil
       :getNextPageParam ad-variants-next-page-param
       :staleTime (* 60 1000)})

(defn ad-variants-by-content-id-query
  [{:keys [advertiser-id adset-id refetch disabled] :as context}
   {:keys [default-sorting] :as filter-params
    :or {default-sorting {:field "content-id" :sort "asc"}}}
   {:keys [^js sort-model ^js filter-model ^js pagination-model] :as table-models}]
  #js {:enabled (and (some? adset-id) (some? pagination-model) (not disabled))
       :queryKey #js ["ad-variants-by-content-id"
                      advertiser-id
                      adset-id
                      sort-model
                      filter-model
                      (some-> pagination-model .-pageSize)]
       :queryFn (fn [^js ctx]
                  (let [{:keys [muix-data-grid] :as query-params}
                        (filter-util/get-query-params
                         (assoc filter-params :default-sorting default-sorting)
                         table-models ctx)]
                    (-> (qhttp/post (qhttp/render :api/content-build-ad-variants-by-content-id
                                                  context
                                                  (dissoc query-params :muix-data-grid))
                                    {:body {:muix-data-grid muix-data-grid}})
                        ;; Ungroups the response to MUIX's endpoint format
                        ;; so that `use-server-table-props` can be used
                        (.then (fn [response]
                                 (let [{:keys [data total]} (->clj response)]
                                   (-> {:total total
                                        :data (for [{{:keys [ad-variants]} :data
                                                     cursor :cursor} data
                                                    ad-variant ad-variants]
                                                {:data ad-variant
                                                 :cursor cursor})}
                                       ->js)))))))
       :getNextPageParam filter-util/default-get-next-page-param
       :staleTime (* 60 1000)
       :refetchInterval (when refetch ad-variants-refetch)})

(defn resize-images [{:keys [advertiser-id feed-id on-success]}]
  #js {:mutationKey #js ["image-editing-mutation" advertiser-id feed-id]
       :mutationFn (fn [{:keys [key width height muix-data-grid]}]
                     (qhttp/post (qhttp/dispatcher-url :automation/resize-images {})
                                 {:body {:advertiser-id advertiser-id
                                         :feed-id feed-id
                                         :key key
                                         :width width
                                         :height height
                                         :muix-data-grid muix-data-grid}}))
       :onSuccess (fn [response]
                    (on-success (->clj response)))})

(defn set-value [{:keys [advertiser-id feed-id on-success]}]
  #js {:mutationKey #js ["set-text-mutation" advertiser-id feed-id]
       :mutationFn (fn [{:keys [key value muix-data-grid]}]
                     (qhttp/post (qhttp/dispatcher-url :automation/set-value {})
                                 {:body {:advertiser-id advertiser-id
                                         :feed-id feed-id
                                         :key key
                                         :value value
                                         :muix-data-grid muix-data-grid}}))
       :onSuccess (fn [response]
                    (on-success (->clj response)))})

(defn shared-ad-variants-query [share-token share-constraints]
  (let [ad-variant-share-constraints (:ad-variants share-constraints)]
    ;; Use different key for this, so this can be invalidated easily without knowing the share-token,
    ;; the client making mutations will only even have one active ad-variants sub (likely?)
    ;; so this should be a good solution.
    #js {:queryKey #js ["shared-ad-variants" share-token (->js ad-variant-share-constraints)]
         :queryFn (fn [^js ctx]
                    (let [cursor (.-pageParam ctx)
                          cursor (when (not (zero? cursor))
                                   cursor)]
                      (qhttp/post (qhttp/render :api/content-build-ad-variants-share-token {})
                                  {:body (merge {:limit 100}
                                                (when (seq ad-variant-share-constraints)
                                                  {:ad-uuids ad-variant-share-constraints})
                                                (if cursor
                                                  {:starting-after cursor}
                                                  {:sort "+created-at"}))
                                   :headers {:Authorization (str "lemonpi " share-token)}})))
         :enabled (and (some? share-token) (some? ad-variant-share-constraints))
         :initialPageParam nil
         :getNextPageParam ad-variants-next-page-param}))

(defn trigger-ad-variants-mutation [{:keys [advertiser-id adset-id]} feed-id]
  #js {:mutationKey #js ["trigger-ad-variants" advertiser-id feed-id]
       :mutationFn (fn []
                     (qhttp/post (qhttp/render :api/content-build-feed-trigger-render {:advertiser-id advertiser-id
                                                                                       :feed-importer-id feed-id})))
       :onSuccess (fn []
                    ;; NOTE: While the ad-variants aren't available right away after triggering,
                    ;; invalidation also sets ouf refetch logic fetch-count to zero,
                    ;; so this triggers the polling.
                    (invalidate-queries {:queryKey ["adset-content-build" advertiser-id adset-id]}))})

(defn share-token-query [{:keys [share-link-id]}]
  #js {:queryKey #js ["ad-variants" "share-token-query" share-link-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/dispatcher-get-share-token {} {"share-link/link-id" share-link-id})
                             {:skip-auth-token true}))
       :enabled (some? share-link-id)
       :staleTime (* 14 60 1000) ; 14 minutes
       :refetchInterval (* 14 60 1000)})

(defn share-link-meta-data-query [{:keys [share-link-id]}]
  #js {:queryKey #js ["ad-variants" "share-link-query" share-link-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/dispatcher-get-share-link-meta-data {} {"share-link/link-id" share-link-id})
                             {:skip-auth-token true}))
       :enabled (not (nil? share-link-id))})

(defn check-for-revoked-links-query [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["ad-variants" "check-for-revoked-links-query" advertiser-id adset-id]
       :queryFn (fn [_ctx]
                  (dispatcher/dispatch {:type :share/get-revoked-links-status
                                        :data {:share-link/page-identifier "ad-variants-preview"
                                               :share-link/entity-ids {:advertiser-id advertiser-id
                                                                       :adset-id adset-id}}}))
       :enabled (and (some? advertiser-id) (some? adset-id))})

(defn review-query [{:keys [adset-id]}]
  #js {:queryKey #js ["ad-review/get-review" adset-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/dispatcher-url :ad-review/get-review {"request/subject-id" adset-id})))
       :enabled (some? adset-id)})

(defn comments-query [{:keys [request-id]}]
  #js {:queryKey #js ["ad-review/get-comments" request-id]
       :enabled (boolean request-id)
       :queryFn (fn [_ctx] (qhttp/get (qhttp/dispatcher-url :ad-review/get-comments
                                                            ;; URL params handler doesn't keep keyword namespace parts currently,
                                                            ;; but using string directly does work
                                                            {"request/id" request-id})))})

(defn history-query [{:keys [request-id]}]
  #js {:queryKey #js ["ad-review/get-history" request-id]
       :enabled (boolean request-id)
       :queryFn (fn [_ctx] (qhttp/get (qhttp/dispatcher-url :ad-review/get-history {"request/id" request-id})))})

(defn assign-status [{:keys [request-id adset-id]}]
  #js {:mutationFn (fn [{:keys [status ad-variant-uuid]}]
                     (qhttp/post (qhttp/dispatcher-url :ad-review/assign-status {})
                                 {:body {:request/id request-id
                                         :review-status/status status
                                         :review-status/subject-id ad-variant-uuid}}))
       :onSuccess (fn [_data {:keys [status _ad-variant-uuid]}]
                    (invalidate-queries {:queryKey ["ad-review/get-history" request-id]})
                    (invalidate-queries {:queryKey ["ad-review/get-review" adset-id]})
                    (if (= status "approved")
                      (rf/dispatch [:toaster-bus/add {:type :success
                                                      :message "Ad variants marked as approved"}])
                      (rf/dispatch
                       [:toaster-bus/add
                        {:type :info
                         :message "Ad variant requires changes"
                         :full-message "Write a comment for the Ad variant to help the team understand what is wrong and what needs to be done to get them accepted."}])))})

(defn add-comment-mutation [{:keys [request-id agency-id advertiser-id campaign-id adset-id selected-ad-variants]}]
  #js {:mutationFn (fn [{:keys [value props]}]
                     (qhttp/post (qhttp/dispatcher-url :ad-review/add-comment {})
                                 {:body (merge props
                                               {:request/id request-id
                                                :comment/meta (cond->
                                                                {:agency-id agency-id
                                                                 :advertiser-id advertiser-id
                                                                 :campaign-id campaign-id
                                                                 :adset-id adset-id}
                                                                (seq selected-ad-variants)
                                                                (assoc :ad-variants selected-ad-variants))
                                                :comment/message value})}))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["ad-review/get-comments" request-id]}))})

(defn edit-comment-mutation [{:keys [request-id]}]
  #js {:mutationFn (fn [{:keys [value props]}]
                     (qhttp/post (qhttp/dispatcher-url :ad-review/edit-comment {})
                                 {:body (merge props
                                               {:request/id request-id
                                                :comment/message value})}))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["ad-review/get-comments" request-id]}))})

(defn set-comment-resolution-mutation [{:keys [request-id]}]
  #js {:mutationFn (fn [{:keys [comment-id resolved]}]
                     (qhttp/post (qhttp/dispatcher-url :ad-review/set-comment-resolution {})
                                 {:body {:comment/id comment-id
                                         :comment/resolved resolved}}))
       :onSuccess (fn [_]
                    ;; history includes comment resolutions, but those are hidden so this isn't needed
                    ;; (invalidate-queries {:queryKey ["ad-review/get-history" request-id]})
                    (invalidate-queries {:queryKey ["ad-review/get-comments" request-id]}))})

(defn delete-comment-mutation [{:keys [request-id]}]
  #js {:mutationFn (fn [{:keys [comment-id]}]
                     (qhttp/post (qhttp/dispatcher-url :ad-review/delete-comment {})
                                 {:body {:comment/id comment-id}}))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["ad-review/get-comments" request-id]}))})

(defn create-share-link-mutation [{:keys [advertiser-id adset-id share-constraints]}]
  #js {:mutationFn (fn [{:keys [selection]}]
                     (let [ad-variant-pairs (get share-constraints selection [])
                           products (get share-constraints "products")]
                       (dispatcher/dispatch {:type :share/create-share-link
                                             :data {:share-link/entity-ids {:advertiser-id advertiser-id
                                                                            :adset-id adset-id}
                                                    :share-link/share-constraints (merge {:ad-variants ad-variant-pairs}
                                                                                         (when products {:products products}))
                                                    :share-link/page-identifier "ad-variants-preview"}})))
       :onSuccess (fn [_resp]
                    ;; Do we need to invalidate? If we increase stale time, then probably
                    (invalidate-queries {:queryKey ["ad-variants" "check-for-revoked-links-query" advertiser-id adset-id]}))
       :onError (fn [_error]
                  (rf/dispatch [:toaster-bus/add {:id ::new-share-link
                                                  :type :error
                                                  :message "There was an error creating the share link"}]))})

(defn revoke-share-link-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [new-status]
                     (dispatcher/dispatch {:type (if (= new-status :revoke)
                                                   :share/revoke-share-links
                                                   :share/restore-revoked-share-links)
                                           :data {:share-link/entity-ids {:advertiser-id advertiser-id
                                                                          :adset-id adset-id}
                                                  :share-link/page-identifier "ad-variants-preview"}}))})

(defn set-approval-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [{:keys [uuids approval]}]
                     (qhttp/put (qhttp/render (if (= approval "ready-and-update-dt")
                                                :api/content-build-approve-and-upgrade
                                                :api/content-build-approve-ad-variant)
                                              {:advertiser-id advertiser-id
                                               :adset-id adset-id
                                               :approval approval})
                                {:body {:ad-uuids uuids}}))
       :onSuccess (fn [^js response {:keys [approval on-success]} _context]
                    ;; TODO handle toaster message for update-dt
                    (let [{:keys [updated not-updated]} (->clj response)
                          base-message (case approval
                                         "approved" "marked as ready"
                                         "rejected" "archived"
                                         "internal-review" "marked for review"
                                         "updated")
                          message (cond-> []
                                    (pos? (count updated))
                                    (conj (str (count updated) " Ad variants were " base-message "."))

                                    (pos? (count not-updated))
                                    (conj (str (count not-updated) " Ad variants were not " base-message "."))
                                    true (->> (str/join " ")))]

                      (invalidate-ad-variant-queries! advertiser-id adset-id)
                      (invalidate-queries {:queryKey ["decision-trees" advertiser-id adset-id]})
                      (invalidate-queries {:queryKey ["decision-tree" advertiser-id adset-id]})

                      (rf/dispatch [:toaster-bus/add
                                    (if (= approval "ready-and-update-dt")
                                      {:type :success
                                       :message "Ad variants were marked as ready and updated in the decision tree"}
                                      {:type (cond
                                               (zero? (count updated)) :error
                                               (= approval "approved") :success
                                               :else :info)
                                       :message message
                                       :full-message (when (not= approval "approved")
                                                       "Write a comment for each Ad variant to help the team understand what is wrong and what needs to be done to get them accepted.")})])
                      (when on-success (on-success))))
       :onError (fn []
                  (rf/dispatch [:toaster-bus/add {:id :approval-mutation-failed
                                                  :type :error
                                                  :message "Status change failed. Please try again or contact support."}]))})

(defn archive-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [{:keys [uuids]}]
                     (qhttp/post (qhttp/render :api/content-build-archive-ad-variant
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 {:body {:ad-uuids uuids}}))
       :onSuccess (fn [^js response {:keys [on-success]} _context]
                    (let [{:keys [updated not-updated]} (->clj response)
                          message (cond-> []
                                    (pos? (count updated))
                                    (conj (str (count updated) " Ad variants were archived."))

                                    (pos? (count not-updated))
                                    (conj (str (count not-updated) " Ad variants were not archived."))
                                    true (->> (str/join " ")))]
                      (invalidate-ad-variant-queries! advertiser-id adset-id)
                      (invalidate-queries {:queryKey ["decision-trees" advertiser-id adset-id]})
                      (invalidate-queries {:queryKey ["decision-tree" advertiser-id adset-id]})
                      (when (seq message)
                        (rf/dispatch [:toaster-bus/add {:type (if (zero? (count updated)) :error :info)
                                                        :message message}]))
                      (when on-success (on-success))))
       :onError (fn []
                  (rf/dispatch [:toaster-bus/add {:id :archive-mutation-failed
                                                  :type :error
                                                  :message "Archive failed. Please try again or contact support."}]))})

(defn unarchive-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [{:keys [uuids]}]
                     (qhttp/post (qhttp/render :api/content-build-unarchive-ad-variant
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 {:body {:ad-uuids uuids}}))
       :onSuccess (fn [^js response {:keys [on-success]} _context]
                    (let [{:keys [updated not-updated]} (->clj response)
                          message (cond-> []
                                    (pos? (count updated))
                                    (conj (str (count updated) " Ad variants were unarchived."))

                                    (pos? (count not-updated))
                                    (conj (str (count not-updated) " Ad variants were not unarchived."))
                                    true (->> (str/join " ")))]
                      (invalidate-ad-variant-queries! advertiser-id adset-id)
                      (when (seq message)
                        (rf/dispatch [:toaster-bus/add {:type (if (zero? (count updated)) :error :info)
                                                        :message message}]))
                      (when on-success (on-success))))
       :onError (fn []
                  (rf/dispatch [:toaster-bus/add {:id :unarchive-mutation-failed
                                                  :type :error
                                                  :message "Unarchive failed. Please try again or contact support."}]))})

(defn ad-builders-upgrade-hints-query [{:keys [adset-id]} & {:keys [show-inactive]}]
  #js {:queryKey #js ["ad-builders-upgrade-hints" adset-id show-inactive]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/adset-ad-builders-upgrade-hints {:adset-id adset-id}
                                           (when show-inactive
                                             {:show-inactive show-inactive}))))
       :enabled (boolean adset-id)})

(defn render-again-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [{:keys [all cancel ad-uuids]}]
                     (qhttp/post (qhttp/render (if all
                                                 (if cancel
                                                   :api/content-build-cancel-all-ad-variant-renders
                                                   :api/content-build-trigger-all-ad-variant-renders)
                                                 (if cancel
                                                   :api/content-build-cancel-ad-variant-renders
                                                   :api/content-build-trigger-ad-variant-renders))
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 (when-not all
                                   {:body {:ad-uuids ad-uuids}})))
       :onSuccess (fn [_data _variables _context]
                    (invalidate-ad-variant-queries! advertiser-id adset-id))})

(defn upload-template-mutation [{:keys [advertiser-id]}]
  #js {:mutationFn (fn [{:keys [file content-id]}]
                     (let [form-data (doto (js/FormData.)
                                       (.append "file" file)
                                       (.append "content-id" content-id)
                                       (.append "advertiser-id" (str advertiser-id)))]
                       (qhttp/post (qhttp/render :api/advertiser-api-manual-upload-template {})
                                   {:body form-data
                                    :format :form-data})))})

(defn upload-ads-mutation [{:keys [advertiser-id adset-id success-message]}]
  #js {:mutationFn (fn [{:keys [ads]}]
                     (qhttp/post (qhttp/render :api/content-build-ads-upload
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 {:body {:ads ads}}))
       :onSuccess (fn [_data _variables _context]
                    (rf/dispatch [:toaster-bus/add {:type :success
                                                    :message (or success-message "Successfully created ad")}])
                    (invalidate-ad-variant-queries! advertiser-id adset-id))

       :onError (fn [_error]
                  (rf/dispatch [:toaster-bus/add {:id :manual-upload-failed
                                                  :type :error
                                                  :message "There was an error creating the ad"}]))})

(defn set-scheduling-mutation [{:keys [advertiser-id adset-id]}]
  #js {:mutationFn (fn [{:keys [ad-uuids scheduling]}]
                     (qhttp/post (qhttp/render :api/content-build-set-scheduling
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 {:body {:ad-uuids ad-uuids
                                         :scheduling scheduling}}))
       :onSuccess (fn [^js response _variables _context]
                    (let [{:keys [updated]} (->clj response)]
                      (invalidate-ad-variant-queries! advertiser-id adset-id)
                      (rf/dispatch [:toaster-bus/add
                                    (if (pos? (count updated))
                                      {:type :success
                                       :message (str "Successfully updated scheduling for " (count updated) " ad variant" (when (> (count updated) 1) "s"))}
                                      {:type :error
                                       :message "No ad variants were updated"})])))
       :onError (fn [_error]
                  (rf/dispatch [:toaster-bus/add {:id :scheduling-update-failed
                                                  :type :error
                                                  :message "Failed to update scheduling"}]))})
